/**
 * Mexican Music Styles, Instruments, and Vocal Styles Constants
 * 
 * Comprehensive collection of Mexican music genres, instruments, and vocal styles
 * for use in song generation and music style selection.
 */

// ============================================================================
// MUSIC STYLES
// ============================================================================

export interface MusicStyle {
  name: string
  description: string
  instruments: string[]
  vocalStyle?: string
  tempo?: 'slow' | 'moderate' | 'fast' | 'very fast' | 'moderate to fast' | 'varies'
  timeSignature?: string
  publicDomainSongs?: string[]
  region?: string
  era?: string
}

export const MEXICAN_MUSIC_STYLES: MusicStyle[] = [
  // Traditional Folk Styles
  {
    name: 'Mariachi',
    description: 'Traditional Mexican folk music characterized by trumpets, violins, guitars, and vihuela. Often features passionate vocals and celebratory themes. Originated in Jalisco in the 19th century.',
    instruments: ['trumpet', 'violin', 'guitar', 'vihuela', 'guitarrón', 'harp'],
    vocalStyle: 'powerful, passionate, with vibrato',
    tempo: 'moderate',
    timeSignature: '3/4, 4/4',
    publicDomainSongs: ['La Cucaracha', 'Cielito Lindo', 'El Son de la Negra'],
    region: 'Jalisco',
    era: '19th century - present',
  },
  {
    name: 'Ranchera',
    description: 'Sentimental country music style expressing love, patriotism, and rural life. Features emotional vocals with dramatic pauses and powerful climaxes.',
    instruments: ['guitar', 'trumpet', 'violin', 'accordion', 'bass'],
    vocalStyle: 'emotional, dramatic, with grito (yell)',
    tempo: 'moderate',
    timeSignature: '3/4, 4/4',
    publicDomainSongs: ['La Paloma', 'Allá en el Rancho Grande'],
    region: 'Rural Mexico',
    era: '1930s - present',
  },
  {
    name: 'Corrido',
    description: 'Narrative ballad style telling stories of heroes, historical events, and social issues. Often features simple instrumentation with focus on storytelling.',
    instruments: ['guitar', 'accordion', 'bass', 'drums'],
    vocalStyle: 'narrative, clear, storytelling',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: ['La Adelita', 'Corrido de Pancho Villa'],
    region: 'Northern Mexico',
    era: '19th century - present',
  },
  {
    name: 'Norteño',
    description: 'Northern Mexican music style featuring accordion and bajo sexto. Upbeat polka-influenced rhythms with themes of love, work, and celebration.',
    instruments: ['accordion', 'bajo sexto', 'bass', 'drums', 'saxophone'],
    vocalStyle: 'energetic, clear, harmonized',
    tempo: 'fast',
    timeSignature: '2/4, 4/4',
    publicDomainSongs: [],
    region: 'Northern Mexico, Texas',
    era: '1930s - present',
  },
  {
    name: 'Banda',
    description: 'Brass band music with heavy emphasis on tuba, trumpets, and percussion. Energetic dance music popular at celebrations and parties.',
    instruments: ['tuba', 'trumpet', 'trombone', 'clarinet', 'saxophone', 'drums', 'tambora'],
    vocalStyle: 'powerful, energetic, with call-and-response',
    tempo: 'fast',
    timeSignature: '2/4, 4/4',
    publicDomainSongs: [],
    region: 'Sinaloa, Western Mexico',
    era: '1990s - present',
  },
  {
    name: 'Cumbia',
    description: 'Colombian-origin dance music adapted in Mexico with distinctive rhythm. Features syncopated percussion and catchy melodies.',
    instruments: ['accordion', 'guitar', 'bass', 'drums', 'conga', 'güiro'],
    vocalStyle: 'rhythmic, melodic, dance-oriented',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Mexico (adapted from Colombia)',
    era: '1960s - present',
  },
  {
    name: 'Son Jarocho',
    description: 'Traditional music from Veracruz featuring harp, jarana, and requinto. Complex rhythms with African, Spanish, and indigenous influences.',
    instruments: ['harp', 'jarana', 'requinto', 'cajón', 'quijada'],
    vocalStyle: 'improvisational, call-and-response',
    tempo: 'fast',
    timeSignature: '6/8, 3/4',
    publicDomainSongs: ['La Bamba'],
    region: 'Veracruz',
    era: '18th century - present',
  },
  {
    name: 'Huapango',
    description: 'Traditional dance music from Huasteca region featuring falsetto vocals and complex violin melodies. Energetic and virtuosic.',
    instruments: ['violin', 'guitar', 'huapanguera', 'jarana huasteca'],
    vocalStyle: 'falsetto, virtuosic, high-pitched',
    tempo: 'fast',
    timeSignature: '3/4, 6/8',
    publicDomainSongs: [],
    region: 'Huasteca (San Luis Potosí, Hidalgo, Veracruz)',
    era: '19th century - present',
  },
  {
    name: 'Bolero',
    description: 'Romantic slow-tempo music with emphasis on emotional lyrics and smooth melodies. Popular for love songs and ballads.',
    instruments: ['guitar', 'piano', 'violin', 'bass', 'strings'],
    vocalStyle: 'smooth, romantic, expressive',
    tempo: 'slow',
    timeSignature: '4/4',
    publicDomainSongs: ['Bésame Mucho', 'Sabor a Mí'],
    region: 'Mexico (adapted from Cuba)',
    era: '1940s - present',
  },
  {
    name: 'Grupera',
    description: 'Modern Mexican pop-rock style combining traditional elements with contemporary sounds. Features electric guitars and synthesizers.',
    instruments: ['electric guitar', 'bass guitar', 'keyboards', 'drums', 'synthesizer'],
    vocalStyle: 'modern pop, clear, harmonized',
    tempo: 'moderate',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Throughout Mexico',
    era: '1980s - present',
  },
  {
    name: 'Tejano',
    description: 'Tex-Mex music blending Mexican and American influences. Features accordion, synthesizers, and bilingual lyrics.',
    instruments: ['accordion', 'synthesizer', 'electric guitar', 'bass', 'drums'],
    vocalStyle: 'bilingual, modern, energetic',
    tempo: 'moderate to fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Texas, Northern Mexico',
    era: '1970s - present',
  },
  {
    name: 'Duranguense',
    description: 'Fast-paced dance music from Durango featuring synthesizers and rapid-fire accordion. Energetic party music.',
    instruments: ['synthesizer', 'accordion', 'drums', 'bass', 'saxophone'],
    vocalStyle: 'rapid, energetic, party-oriented',
    tempo: 'very fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Durango',
    era: '2000s - present',
  },
  {
    name: 'Salsa',
    description: 'Upbeat dance music with complex rhythms and brass sections. Adapted in Mexico with local influences.',
    instruments: ['trumpet', 'trombone', 'piano', 'bass', 'conga', 'bongo', 'timbales'],
    vocalStyle: 'energetic, rhythmic, call-and-response',
    tempo: 'fast',
    timeSignature: '4/4',
    publicDomainSongs: [],
    region: 'Mexico (adapted from Caribbean)',
    era: '1970s - present',
  },
  {
    name: 'Vals Mexicano',
    description: 'Mexican waltz style with 3/4 time signature. Elegant and graceful dance music often used for celebrations.',
    instruments: ['guitar', 'violin', 'piano', 'bass', 'harp'],
    vocalStyle: 'elegant, smooth, romantic',
    tempo: 'moderate',
    timeSignature: '3/4',
    publicDomainSongs: ['Sobre las Olas'],
    region: 'Throughout Mexico',
    era: '19th century - present',
  },
  {
    name: 'Chilena',
    description: 'Music from Guerrero and Oaxaca with Chilean influences. Features guitar and distinctive rhythmic patterns.',
    instruments: ['guitar', 'violin', 'harp', 'cajón'],
    vocalStyle: 'melodic, narrative',
    tempo: 'moderate',
    timeSignature: '3/4, 6/8',
    publicDomainSongs: [],
    region: 'Guerrero, Oaxaca',
    era: '19th century - present',
  },
]

// ============================================================================
// INSTRUMENTS
// ============================================================================

export enum MexicanInstrument {
  // String Instruments
  GUITAR = 'guitar',
  VIHUELA = 'vihuela',
  GUITARRON = 'guitarrón',
  JARANA = 'jarana',
  REQUINTO = 'requinto',
  HUAPANGUERA = 'huapanguera',
  JARANA_HUASTECA = 'jarana huasteca',
  HARP = 'harp',
  VIOLIN = 'violin',
  BAJO_SEXTO = 'bajo sexto',
  ELECTRIC_GUITAR = 'electric guitar',
  BASS_GUITAR = 'bass guitar',
  
  // Wind Instruments
  TRUMPET = 'trumpet',
  TROMBONE = 'trombone',
  TUBA = 'tuba',
  SAXOPHONE = 'saxophone',
  CLARINET = 'clarinet',
  FLUTE = 'flute',
  
  // Keyboard Instruments
  ACCORDION = 'accordion',
  PIANO = 'piano',
  KEYBOARD = 'keyboard',
  SYNTHESIZER = 'synthesizer',
  
  // Percussion Instruments
  DRUMS = 'drums',
  CONGA = 'conga',
  BONGO = 'bongo',
  TIMBALES = 'timbales',
  CAJON = 'cajón',
  TAMBORA = 'tambora',
  GUIRO = 'güiro',
  MARACAS = 'maracas',
  QUIJADA = 'quijada',
  
  // Other
  BASS = 'bass',
  STRINGS = 'strings',
}

export const INSTRUMENT_DESCRIPTIONS: Record<MexicanInstrument, string> = {
  [MexicanInstrument.GUITAR]: 'Six-string acoustic guitar, fundamental in most Mexican music styles',
  [MexicanInstrument.VIHUELA]: 'Small five-string guitar used in mariachi, higher pitched than guitar',
  [MexicanInstrument.GUITARRON]: 'Large bass guitar used in mariachi, provides rhythmic foundation',
  [MexicanInstrument.JARANA]: 'Small eight-string guitar from Veracruz, used in son jarocho',
  [MexicanInstrument.REQUINTO]: 'Small four-string guitar, higher pitched, used in son jarocho',
  [MexicanInstrument.HUAPANGUERA]: 'Large eight-string guitar used in huapango music',
  [MexicanInstrument.JARANA_HUASTECA]: 'Small five-string guitar from Huasteca region',
  [MexicanInstrument.HARP]: 'Large harp used in traditional Mexican music, especially son jarocho',
  [MexicanInstrument.VIOLIN]: 'String instrument providing melody in mariachi and traditional styles',
  [MexicanInstrument.BAJO_SEXTO]: 'Twelve-string bass guitar used in norteño music',
  [MexicanInstrument.ELECTRIC_GUITAR]: 'Electric guitar used in modern Mexican pop and rock',
  [MexicanInstrument.BASS_GUITAR]: 'Electric bass providing low-end rhythm',
  
  [MexicanInstrument.TRUMPET]: 'Brass instrument providing bright melodies in mariachi and banda',
  [MexicanInstrument.TROMBONE]: 'Brass instrument with slide, common in banda and salsa',
  [MexicanInstrument.TUBA]: 'Large brass instrument providing bass in banda music',
  [MexicanInstrument.SAXOPHONE]: 'Woodwind instrument used in banda and norteño',
  [MexicanInstrument.CLARINET]: 'Woodwind instrument common in banda music',
  [MexicanInstrument.FLUTE]: 'Woodwind instrument used in some traditional styles',
  
  [MexicanInstrument.ACCORDION]: 'Keyboard instrument essential to norteño and tejano music',
  [MexicanInstrument.PIANO]: 'Keyboard instrument used in bolero and modern styles',
  [MexicanInstrument.KEYBOARD]: 'Electronic keyboard used in modern Mexican music',
  [MexicanInstrument.SYNTHESIZER]: 'Electronic instrument used in duranguense and modern styles',
  
  [MexicanInstrument.DRUMS]: 'Drum set providing rhythm in modern Mexican music',
  [MexicanInstrument.CONGA]: 'Tall hand drum used in salsa and cumbia',
  [MexicanInstrument.BONGO]: 'Pair of small drums used in salsa',
  [MexicanInstrument.TIMBALES]: 'Shallow drums used in salsa',
  [MexicanInstrument.CAJON]: 'Box drum played by hand, used in son jarocho',
  [MexicanInstrument.TAMBORA]: 'Large drum used in banda music',
  [MexicanInstrument.GUIRO]: 'Scraped gourd instrument providing rhythm',
  [MexicanInstrument.MARACAS]: 'Shaken rattles providing rhythm',
  [MexicanInstrument.QUIJADA]: 'Donkey jawbone used as percussion in son jarocho',
  
  [MexicanInstrument.BASS]: 'Acoustic or electric bass providing low-end foundation',
  [MexicanInstrument.STRINGS]: 'String section (violins, violas, cellos) used in orchestral arrangements',
}

// ============================================================================
// VOCAL STYLES
// ============================================================================

export enum VocalStyle {
  POWERFUL_PASSIONATE = 'powerful, passionate, with vibrato',
  EMOTIONAL_DRAMATIC = 'emotional, dramatic, with grito (yell)',
  NARRATIVE_CLEAR = 'narrative, clear, storytelling',
  ENERGETIC_HARMONIZED = 'energetic, clear, harmonized',
  POWERFUL_ENERGETIC = 'powerful, energetic, with call-and-response',
  RHYTHMIC_MELODIC = 'rhythmic, melodic, dance-oriented',
  IMPROVISATIONAL = 'improvisational, call-and-response',
  FALSETTO_VIRTUOSIC = 'falsetto, virtuosic, high-pitched',
  SMOOTH_ROMANTIC = 'smooth, romantic, expressive',
  MODERN_POP = 'modern pop, clear, harmonized',
  BILINGUAL_MODERN = 'bilingual, modern, energetic',
  RAPID_ENERGETIC = 'rapid, energetic, party-oriented',
  ELEGANT_SMOOTH = 'elegant, smooth, romantic',
  MELODIC_NARRATIVE = 'melodic, narrative',
}

export const VOCAL_STYLE_DESCRIPTIONS: Record<VocalStyle, string> = {
  [VocalStyle.POWERFUL_PASSIONATE]: 'Strong, emotional vocals with vibrato, typical of mariachi',
  [VocalStyle.EMOTIONAL_DRAMATIC]: 'Dramatic emotional expression with grito (yell), characteristic of ranchera',
  [VocalStyle.NARRATIVE_CLEAR]: 'Clear storytelling vocals for narrative ballads like corridos',
  [VocalStyle.ENERGETIC_HARMONIZED]: 'Energetic, clear vocals with harmonies, common in norteño',
  [VocalStyle.POWERFUL_ENERGETIC]: 'Powerful vocals with call-and-response patterns, typical of banda',
  [VocalStyle.RHYTHMIC_MELODIC]: 'Rhythmic, dance-oriented vocals with melodic lines, used in cumbia',
  [VocalStyle.IMPROVISATIONAL]: 'Improvisational vocals with call-and-response, characteristic of son jarocho',
  [VocalStyle.FALSETTO_VIRTUOSIC]: 'High-pitched falsetto vocals showcasing virtuosity, typical of huapango',
  [VocalStyle.SMOOTH_ROMANTIC]: 'Smooth, romantic vocals expressing emotion, used in bolero',
  [VocalStyle.MODERN_POP]: 'Modern pop-style vocals with clear harmonies, used in grupera',
  [VocalStyle.BILINGUAL_MODERN]: 'Bilingual vocals blending Spanish and English, characteristic of tejano',
  [VocalStyle.RAPID_ENERGETIC]: 'Rapid-fire energetic vocals for party music, typical of duranguense',
  [VocalStyle.ELEGANT_SMOOTH]: 'Elegant, smooth vocals for waltz and formal dance music',
  [VocalStyle.MELODIC_NARRATIVE]: 'Melodic vocals with narrative elements, used in chilena',
}

// ============================================================================
// INSTRUMENT COMBINATIONS (PRESETS)
// ============================================================================

export interface InstrumentPreset {
  name: string
  description: string
  instruments: MexicanInstrument[]
  style: string
}

export const INSTRUMENT_PRESETS: InstrumentPreset[] = [
  {
    name: 'Mariachi Classic',
    description: 'Traditional mariachi ensemble',
    instruments: [
      MexicanInstrument.TRUMPET,
      MexicanInstrument.VIOLIN,
      MexicanInstrument.GUITAR,
      MexicanInstrument.VIHUELA,
      MexicanInstrument.GUITARRON,
    ],
    style: 'Mariachi',
  },
  {
    name: 'Norteño Standard',
    description: 'Classic norteño instrumentation',
    instruments: [
      MexicanInstrument.ACCORDION,
      MexicanInstrument.BAJO_SEXTO,
      MexicanInstrument.BASS,
      MexicanInstrument.DRUMS,
    ],
    style: 'Norteño',
  },
  {
    name: 'Banda Full',
    description: 'Complete banda brass section',
    instruments: [
      MexicanInstrument.TUBA,
      MexicanInstrument.TRUMPET,
      MexicanInstrument.TROMBONE,
      MexicanInstrument.SAXOPHONE,
      MexicanInstrument.CLARINET,
      MexicanInstrument.TAMBORA,
    ],
    style: 'Banda',
  },
  {
    name: 'Son Jarocho',
    description: 'Traditional Veracruz ensemble',
    instruments: [
      MexicanInstrument.HARP,
      MexicanInstrument.JARANA,
      MexicanInstrument.REQUINTO,
      MexicanInstrument.CAJON,
      MexicanInstrument.QUIJADA,
    ],
    style: 'Son Jarocho',
  },
  {
    name: 'Modern Pop',
    description: 'Contemporary Mexican pop instrumentation',
    instruments: [
      MexicanInstrument.ELECTRIC_GUITAR,
      MexicanInstrument.BASS_GUITAR,
      MexicanInstrument.KEYBOARD,
      MexicanInstrument.DRUMS,
      MexicanInstrument.SYNTHESIZER,
    ],
    style: 'Grupera',
  },
]

// ============================================================================
// SEARCH HELPERS
// ============================================================================

export interface SearchableMusicItem {
  name: string
  description: string
  type: 'style' | 'instrument' | 'vocal' | 'preset'
  keywords: string[]
}

export function getAllSearchableItems(): SearchableMusicItem[] {
  const styles: SearchableMusicItem[] = MEXICAN_MUSIC_STYLES.map(style => ({
    name: style.name,
    description: style.description,
    type: 'style',
    keywords: [
      ...style.name.toLowerCase().split(' '),
      ...style.description.toLowerCase().split(' '),
      ...style.instruments,
      ...(style.region ? [style.region.toLowerCase()] : []),
      ...(style.era ? [style.era.toLowerCase()] : []),
    ],
  }))

  const instruments: SearchableMusicItem[] = Object.values(MexicanInstrument).map(instrument => ({
    name: instrument,
    description: INSTRUMENT_DESCRIPTIONS[instrument],
    type: 'instrument',
    keywords: [
      ...instrument.toLowerCase().split(' '),
      ...INSTRUMENT_DESCRIPTIONS[instrument].toLowerCase().split(' '),
    ],
  }))

  const vocals: SearchableMusicItem[] = Object.values(VocalStyle).map(style => ({
    name: style,
    description: VOCAL_STYLE_DESCRIPTIONS[style],
    type: 'vocal',
    keywords: [
      ...style.toLowerCase().split(' '),
      ...VOCAL_STYLE_DESCRIPTIONS[style].toLowerCase().split(' '),
    ],
  }))

  const presets: SearchableMusicItem[] = INSTRUMENT_PRESETS.map(preset => ({
    name: preset.name,
    description: preset.description,
    type: 'preset',
    keywords: [
      ...preset.name.toLowerCase().split(' '),
      ...preset.description.toLowerCase().split(' '),
      ...preset.instruments.map(i => i.toLowerCase()),
      preset.style.toLowerCase(),
    ],
  }))

  return [...styles, ...instruments, ...vocals, ...presets]
}

